import React, { useState } from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClockRotateLeft, faStar } from "@fortawesome/free-solid-svg-icons";

import DpiLabel from "./components/dpi-label";
import RejectReason from "./components/reject-reason";
import SkipEditing from "./components/skip-editing";
import UneditedImage from "./components/unedited-image";
import UploadImage from "./components/upload-image";
import Gs1Modal from "components/photography/components/gs1-modal";

import { VALID_DPI } from "./lib/constants";
import { DEFAULT_LOCALE, EDITING_SOURCE_MAP, CODING_SHOT } from "../photography/lib/helpers";
import { JobStatus } from "./components/job-status";

const shouldShowDPI = (dpi) => dpi && dpi !== VALID_DPI;

const ImageItem = ({ shot, sourcePage, ...props }) => {
    const canAdd = !props.isCompleted && !props.deleteButton && shot.imageViewDescription && !shot.isMultipleImageEligible;
    const [open, setOpen] = useState(false);

    const handleOpen = (isOpen) => setOpen(isOpen);

    const updateSkip = () => {
        const useValue =
            shot.editingSourceId !== EDITING_SOURCE_MAP["skipEditing"] ? EDITING_SOURCE_MAP["skipEditing"] : EDITING_SOURCE_MAP["internal"];
        props.changeShotProperty(props.wipKey, shot.key, "editingSourceId", useValue);
    };

    const isDefaultImageView = props.defaultImageView === shot.imageView && (!shot.localeCode || shot.localeCode === DEFAULT_LOCALE.value);
    const isEcom = shot.imageView === "Ecom";
    const allowSkip = !shot.isPremiumEditing && !isDefaultImageView && !isEcom;
    const uploadOrSkip =
        shot.editingSourceId === EDITING_SOURCE_MAP["skipEditing"] ? (
            <SkipEditing
                canAdd={canAdd}
                updateSkip={updateSkip}
                isCompleted={props.isCompleted}
                shot={shot}
            />
        ) : (
            <UploadImage
                wipKey={props.wipKey}
                taskKey={props.taskKey}
                shot={shot}
                updateSkip={updateSkip}
                rotateShot={props.rotateShot}
                updateShot={props.updateShot}
                isRotating={props.isRotating}
                isCompleted={props.isCompleted}
                allowSkip={allowSkip}
                mimeType={props.mimeType}
                hasPegholeData={props.hasPegholeData}
                shipmentKey={props.shipmentKey}
                shipmentProductKey={props.shipmentProductKey}
                sourcePage={sourcePage}
                roUpload={props.readOnlyUpload}
                roDownload={props.readOnlyDownload}
                roSkipEditing={props.readOnlySkipEditing}
                isIE={props.isIE}
            >
                {shouldShowDPI(shot.editedDPIValue) && (
                    <DpiLabel
                        dpi={shot.editedDPIValue}
                        height={shot.editedHeight}
                        width={shot.editedWidth}
                    />
                )}
            </UploadImage>
        );

    const UneditedImageOrSkip = shot.isGroupShot ? (
        <SkipEditing
            canAdd={canAdd}
            removeShotByKey={props.removeShotByKey}
            deleteButton={true}
            wipKey={props.wipKey}
            shotKey={shot && shot.key}
            updateSkip={updateSkip}
            uneditedImageSkip={shot.imageView}
            isCompleted={props.isCompleted}
            isReadOnly={props.readOnlySkipEditing}
        />
    ) : (
        <UneditedImage
            wipKey={props.wipKey}
            shot={shot}
            mimeType={props.mimeType}
            shipmentKey={props.shipmentKey}
            shipmentProductKey={props.shipmentProductKey}
            sourcePage={sourcePage}
            roDownload={props.readOnlyDownload}
        >
            {shouldShowDPI(shot.uneditedDPIValue) && (
                <DpiLabel
                    dpi={shot.uneditedDPIValue}
                    height={shot.uneditedHeight}
                    width={shot.uneditedWidth}
                />
            )}
        </UneditedImage>
    );

    return (
        <>
            <JobStatus
                notificationHistory={shot.brightRiverNotificationHistory}
                open={open}
                onOpen={handleOpen}
            />
            <div
                id={`shot_${shot.key}`}
                className={classnames("editing-item", { "-reject": !!shot.editingJobFileRejectTypeId })}
            >
                <h3>
                    {shot.isPremiumEditing && (
                        <span className="star">
                            <FontAwesomeIcon icon={faStar} />{" "}
                        </span>
                    )}
                    <div>
                        {shot.imageViewDescription || CODING_SHOT.imageViewDescription}
                        {props.showEcom && props.defaultImageView && isDefaultImageView ? " (Ecom)" : ""}
                        <span className="memo">&nbsp;{shot.locale || DEFAULT_LOCALE.text}</span>
                        {!!shot.gs1MetadataShortDescription && (
                            <div>
                                <em className="memo">GS1 Code: {shot.gs1MetadataShortDescription}</em>
                                <Gs1Modal
                                    image={shot}
                                    wipKey={props.wipKey}
                                    metadata={shot.hasGs1Metadata}
                                    changeIEGs1={props.changeGs1Code}
                                />
                            </div>
                        )}

                        {shot.userFriendlyStatusMessage && (
                            <div className="align-items-center">
                                <em className="memo">Status: {shot.userFriendlyStatusMessage}</em>
                                {shot.brightRiverNotificationHistory && shot.brightRiverNotificationHistory.length > 0 && (
                                    <FontAwesomeIcon
                                        onClick={() => setOpen(true)}
                                        className="m-l-12 cursor-pointer"
                                        icon={faClockRotateLeft}
                                    />
                                )}
                            </div>
                        )}
                        {!!shot.editingJobId && (
                            <div>
                                <em className="memo">Job Id: {shot.editingJobId}</em>
                            </div>
                        )}
                        {!!shot.editingJobFileId && (
                            <div>
                                <em className="memo">Job File Id: {shot.editingJobFileId}</em>
                            </div>
                        )}
                    </div>
                    {!props.readOnlySkipEditing && (
                        <RejectReason
                            shot={shot}
                            update={props.updateRejectReason}
                            rejectReasons={props.rejectReasons}
                            isQA={props.isQA}
                            onRemoveRejectReason={props.onRemoveRejectReason}
                            isCompleted={props.isCompleted}
                        />
                    )}
                </h3>
                {UneditedImageOrSkip}
                {uploadOrSkip}
            </div>
        </>
    );
};

export default ImageItem;
